import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { generateId } from '@sb/shared/util';
import { clsx } from 'clsx';

import { finishRunningCycle, restoreRunningCycle, useCheckmarkMorph } from './branded-loader-animation';
import { LOGO_BOTTOM_BAR, LOGO_TOP_BAR } from './branded-loader-shapes';

/** `'flash'` lights the checkmark up in the Spark gradient, then settles back to the loader colour. */
export type BrandedLoaderCheckColor = 'flash' | 'gradient' | (string & {});

/** `'unset'` leaves the width to a class set by the consumer; the height always follows the logo. */
export type BrandedLoaderSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'unset';

type BrandedLoaderPhase = 'loading' | 'finishing' | 'done';

const SIZE_CLASSES: Record<BrandedLoaderSize, string> = {
  xs: clsx('w-4'), // 16px
  sm: clsx('w-5'), // 20px
  md: clsx('w-6'), // 24px
  lg: clsx('w-12'), // 48px
  xl: clsx('w-16'), // 64px
  unset: '',
};

@Component({
  selector: 'sb-branded-loader',
  templateUrl: './branded-loader.component.html',
  imports: [TranslatePipe],
  host: {
    role: 'status',
    '[class]': 'hostClasses()',
    '[style.color]': 'color()',
  },
})
export class BrandedLoaderComponent {
  #host: ElementRef<HTMLElement> = inject(ElementRef);

  /** Finishes the running cycle, then morphs into a checkmark. Back to `false` restarts the loop. */
  done = input(false);
  size = input<BrandedLoaderSize>('md');
  /** Any CSS colour; the surrounding text colour when unset. */
  color = input<string>();
  checkColor = input<BrandedLoaderCheckColor>('flash');
  /** Milliseconds before the loader appears, so fast actions don't flash it. A done state always shows. */
  delay = input(300);
  loadingLabel = input<string>();
  doneLabel = input<string>();

  /** Emits once the checkmark has fully formed. */
  checkmarkShown = output<void>();

  gradientId = generateId('sb-branded-loader-spark');
  logoTopBar = LOGO_TOP_BAR;
  logoBottomBar = LOGO_BOTTOM_BAR;

  checkmark = useCheckmarkMorph({
    flashFadesOut: computed(() => this.checkColor() === 'flash'),
    onFormed: () => this.checkmarkShown.emit(),
  });

  phase = signal<BrandedLoaderPhase>('loading');

  #delayElapsed = signal(false);
  #run = 0;
  #finishingAnimations: Animation[] = [];

  hostClasses = computed(() =>
    clsx('relative inline-block aspect-branded-loader shrink-0 align-middle', SIZE_CLASSES[this.size()]),
  );

  isShown = computed(() => this.#delayElapsed() || this.phase() !== 'loading');
  isDone = computed(() => this.phase() === 'done');

  checkmarkFill = computed(() => {
    const checkColor = this.checkColor();

    return checkColor === 'flash' || checkColor === 'gradient' ? `url(#${this.gradientId})` : checkColor;
  });

  constructor() {
    effect((onCleanup) => {
      if (this.done()) {
        untracked(() => this.#finish());
        return;
      }

      const delay = untracked(() => {
        this.#reset();
        return this.delay();
      });

      if (delay <= 0) {
        this.#delayElapsed.set(true);
        return;
      }

      const timer = setTimeout(() => this.#delayElapsed.set(true), delay);
      onCleanup(() => clearTimeout(timer));
    });

    // Removing the host cancels the loop animations, which resolves a pending finish.
    inject(DestroyRef).onDestroy(() => this.#run++);
  }

  #finish(): void {
    if (this.phase() !== 'loading') {
      return;
    }

    const animations = this.#delayElapsed() ? (this.#host.nativeElement.getAnimations?.({ subtree: true }) ?? []) : [];

    if (animations.length === 0) {
      this.#showCheckmark();
      return;
    }

    const run = this.#run;
    this.#finishingAnimations = animations;
    this.phase.set('finishing');

    finishRunningCycle(animations).then(() => {
      if (run === this.#run) {
        this.#showCheckmark();
      }
    });
  }

  #showCheckmark(): void {
    this.#finishingAnimations = [];
    this.phase.set('done');
    this.checkmark.start();
  }

  #reset(): void {
    this.#run++;
    this.checkmark.stop();
    restoreRunningCycle(this.#finishingAnimations);

    this.#finishingAnimations = [];
    this.#delayElapsed.set(false);
    this.phase.set('loading');
  }
}
